/**
 * `<caos-log-list>` — which logs exist, who caused them and which one is worth opening.
 * (Catalog: Developer surfaces › LogList — the logs an environment has recorded, with who caused each one and how big it is.)
 *
 * THE LIST IS A CHOOSING SURFACE, not a reading one. Nobody reads a log list; they scan it for the
 * one log that belongs to the thing that just went wrong. Everything follows: newest first, the
 * person and the operation in full, and the size stated because a 4 MB log and a 2 KB log are
 * different decisions about whether to open it.
 *
 * TIME IS SHOWN AS BOTH. "2 minutes ago" is what somebody actually needs — the log they want is the
 * one from just now — and the absolute time is what they need the moment they are comparing against
 * anything else, a report, a ticket, somebody else's screen. Showing only the relative form makes a
 * log list useless for the second task, and showing only the absolute form makes it slow for the
 * first, so both are there and the absolute one is the title.
 *
 * SELECTION IS FOR DELETING, and deleting is asked for rather than done. A log list fills up and
 * clearing it is a routine act, but it is still an irreversible one, and a component that removed
 * rows on a click would be deciding on somebody's behalf that no confirmation was warranted.
 *
 * Styling seams: `::part(logs)`, `::part(summary)`, `::part(toolbar)`, `::part(search)`,
 * `::part(actions)`, `::part(table)`, `::part(open)`, `::part(when)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './data-table.js';
import type { CaosDataTable, CaosSortDirection, CaosTableRow } from './data-table.js';

/** One recorded log. */
export interface CaosLogEntry {
  /** The log's id. */
  readonly id: string;
  /** Who caused it. */
  readonly person?: string;
  /** Which application it came from. */
  readonly application?: string;
  /** What was being done. */
  readonly operation?: string;
  /** When it was recorded, as an ISO timestamp. */
  readonly at?: string;
  /** How it ended — `Success`, `Failed`, or whatever the platform recorded. */
  readonly status?: string;
  /** How big it is, in bytes. */
  readonly bytes?: number;
}

const sheet = makeSheet(`
  :host { display: block; }

  .logs { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .toolbar { display: flex; gap: var(--caos-space-2); align-items: center; flex-wrap: wrap; }
  .toolbar input {
    flex: 1 1 200px;
    min-width: 160px;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 9px;
  }
  .toolbar input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .actions { display: inline-flex; gap: var(--caos-space-2); }
  .actions button {
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12.5px;
    padding: 5px 10px;
    cursor: pointer;
  }
  .actions button:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .actions button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .actions button.delete:not(:disabled) { color: var(--caos-color-danger); border-color: var(--caos-color-danger); }

  .summary { font-family: var(--caos-font); font-size: 12.5px; color: var(--caos-color-text-muted); }
  .summary strong { color: var(--caos-color-text); font-weight: 600; }

  .empty { font-family: var(--caos-font); font-size: 13px; color: var(--caos-color-text-muted); }
`);

/** A byte count as a person reads it. */
export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * How long ago, in words. `now` is a parameter rather than a call to `Date.now()` so this is a
 * function of its inputs and a test can measure it without owning the clock.
 */
export function timeAgo(at: string | undefined, now: number): string {
  if (at === undefined) return '';
  const then = Date.parse(at);
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

export class CaosLogList extends CaosElement {
  static get observedAttributes(): string[] {
    return ['search', 'selected-id', 'sort-key', 'sort-direction', 'empty-text', 'deletable'];
  }

  private summaryEl: HTMLParagraphElement;
  private searchEl: HTMLInputElement;
  private refreshEl: HTMLButtonElement;
  private deleteEl: HTMLButtonElement;
  private tableEl: CaosDataTable;
  private emptyEl: HTMLParagraphElement;

  private data: readonly CaosLogEntry[] = [];
  private chosen = new Set<string>();
  /** Fixed at render time so every row on one screen measures "ago" from the same instant. */
  private renderedAt = Date.now();

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'logs';
    wrap.setAttribute('part', 'logs');

    const toolbar = document.createElement('div');
    toolbar.className = 'toolbar';
    toolbar.setAttribute('part', 'toolbar');

    this.searchEl = document.createElement('input');
    this.searchEl.type = 'search';
    this.searchEl.setAttribute('part', 'search');
    this.searchEl.placeholder = 'Search logs';
    this.searchEl.setAttribute('aria-label', 'Search logs by person, application, operation or status');
    this.searchEl.addEventListener('input', () => this.setAttribute('search', this.searchEl.value));

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    this.refreshEl = document.createElement('button');
    this.refreshEl.type = 'button';
    this.refreshEl.textContent = 'Refresh';
    this.refreshEl.addEventListener('click', () =>
      this.dispatchEvent(new CustomEvent('caos-log-refresh', { bubbles: true, composed: true })),
    );
    this.deleteEl = document.createElement('button');
    this.deleteEl.type = 'button';
    this.deleteEl.className = 'delete';
    this.deleteEl.addEventListener('click', () => this.requestDelete());
    actions.append(this.refreshEl, this.deleteEl);

    toolbar.append(this.searchEl, actions);

    this.summaryEl = document.createElement('p');
    this.summaryEl.className = 'summary';
    this.summaryEl.setAttribute('part', 'summary');
    this.summaryEl.setAttribute('role', 'status');

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.hidden = true;

    this.tableEl = document.createElement('caos-data-table') as CaosDataTable;
    this.tableEl.setAttribute('part', 'table');
    this.tableEl.selectable = true;
    this.tableEl.addEventListener('caos-sort', (e) => {
      const { column, direction } = (e as CustomEvent<{ column: string; direction: CaosSortDirection }>).detail;
      e.stopPropagation();
      this.setAttribute('sort-key', column);
      this.setAttribute('sort-direction', direction);
    });
    const remember = (e: Event): void => {
      this.chosen = new Set((e as CustomEvent<{ selectedIds: string[] }>).detail.selectedIds);
      this.renderState();
    };
    this.tableEl.addEventListener('caos-row-select', remember);
    this.tableEl.addEventListener('caos-select-all', remember);

    wrap.append(toolbar, this.summaryEl, this.emptyEl, this.tableEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(name: string, _before: string | null, after: string | null): void {
    if (name === 'search' && this.searchEl.value !== (after ?? '')) this.searchEl.value = after ?? '';
    this.render();
  }

  get logs(): readonly CaosLogEntry[] {
    return this.data;
  }

  set logs(next: readonly CaosLogEntry[]) {
    this.data = Array.isArray(next) ? next : [];
    const known = new Set(this.data.map((l) => l.id));
    for (const id of [...this.chosen]) if (!known.has(id)) this.chosen.delete(id);
    this.render();
  }

  /** The logs picked for an action. */
  get selectedIds(): readonly string[] {
    return [...this.chosen];
  }

  /** The logs surviving the search, in the current order. */
  get visibleLogs(): readonly CaosLogEntry[] {
    const term = (this.getAttribute('search') ?? '').trim().toLowerCase();
    const kept =
      term === ''
        ? this.data
        : this.data.filter((l) =>
            [l.id, l.person, l.application, l.operation, l.status]
              .filter((v): v is string => typeof v === 'string')
              .some((v) => v.toLowerCase().includes(term)),
          );
    return this.order(kept);
  }

  private order(logs: readonly CaosLogEntry[]): readonly CaosLogEntry[] {
    const key = this.getAttribute('sort-key') ?? 'at';
    const asked = this.getAttribute('sort-direction');
    // NEWEST FIRST is the default, because the log somebody wants is almost always the last one.
    // That is descending, which is the opposite of every other column's natural direction — so it
    // is stated here rather than left to a caller to remember.
    const sign = (asked ?? (key === 'at' ? 'desc' : 'asc')) === 'desc' ? -1 : 1;
    return [...logs].sort((a, b) => {
      if (key === 'bytes') {
        const x = a.bytes;
        const y = b.bytes;
        if (x === undefined && y === undefined) return a.id.localeCompare(b.id);
        if (x === undefined) return 1;
        if (y === undefined) return -1;
        if (x !== y) return (x - y) * sign;
        return a.id.localeCompare(b.id);
      }
      const read = (l: CaosLogEntry): string => String((l as unknown as Record<string, unknown>)[key] ?? '');
      const x = read(a);
      const y = read(b);
      if (x === '' && y === '') return a.id.localeCompare(b.id);
      if (x === '') return 1;
      if (y === '') return -1;
      const cmp = x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });
      return (cmp !== 0 ? cmp : a.id.localeCompare(b.id)) * sign;
    });
  }

  private open(log: CaosLogEntry): void {
    this.setAttribute('selected-id', log.id);
    this.dispatchEvent(new CustomEvent('caos-log-open', { bubbles: true, composed: true, detail: { log } }));
  }

  private requestDelete(): void {
    if (this.chosen.size === 0) return;
    // Asked for, never done. Clearing a log list is routine and still irreversible, and a component
    // that removed rows on a click would be deciding on somebody's behalf that no confirmation was
    // warranted.
    this.dispatchEvent(
      new CustomEvent('caos-log-delete', { bubbles: true, composed: true, detail: { ids: this.selectedIds } }),
    );
  }

  private render(): void {
    this.renderedAt = Date.now();
    const visible = this.visibleLogs;
    const empty = visible.length === 0;
    this.emptyEl.hidden = !empty;
    this.tableEl.hidden = empty;
    this.emptyEl.textContent = this.getAttribute('empty-text') ?? 'No logs recorded.';

    if (!empty) {
      this.tableEl.columns = [
        { key: 'at', label: 'When', sortable: true, width: '16%' },
        { key: 'person', label: 'Person', sortable: true, width: '16%' },
        { key: 'application', label: 'Application', sortable: true, width: '16%' },
        { key: 'operation', label: 'Operation', sortable: true },
        { key: 'status', label: 'Status', sortable: true, width: '12%' },
        { key: 'bytes', label: 'Size', sortable: true, align: 'end', width: '10%' },
        { key: 'open', label: '', width: '8%' },
      ];
      this.tableEl.sort = {
        key: this.getAttribute('sort-key') ?? 'at',
        direction: (this.getAttribute('sort-direction') ?? 'desc') === 'desc' ? 'desc' : 'asc',
      };
      this.tableEl.rows = visible.map((l) => this.rowFor(l));
    }

    this.renderState();
  }

  private renderState(): void {
    const visible = this.visibleLogs.length;
    const nodes: Node[] = [];
    const strong = document.createElement('strong');
    strong.textContent =
      visible === this.data.length
        ? `${this.data.length} ${this.data.length === 1 ? 'log' : 'logs'}`
        : `${visible} of ${this.data.length} logs`;
    nodes.push(strong);
    if (this.chosen.size > 0) nodes.push(document.createTextNode(` · ${this.chosen.size} selected`));
    this.summaryEl.replaceChildren(...nodes);

    this.deleteEl.textContent = this.chosen.size > 0 ? `Delete ${this.chosen.size}` : 'Delete';
    this.deleteEl.disabled = this.chosen.size === 0;
    this.deleteEl.hidden = !this.hasAttribute('deletable');
  }

  private rowFor(log: CaosLogEntry): CaosTableRow {
    const when = document.createElement('span');
    when.setAttribute('part', 'when');
    // Relative for the reading, absolute as the title — the log somebody wants is the one from just
    // now, and the absolute time is what they need the moment they compare it against anything else.
    when.textContent = timeAgo(log.at, this.renderedAt) || (log.at ?? '');
    if (log.at !== undefined) when.title = log.at;

    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'caos-table__idlink';
    open.setAttribute('part', 'open');
    open.textContent = 'Open';
    open.setAttribute('aria-label', `Open the log from ${timeAgo(log.at, this.renderedAt) || log.id}`);
    open.addEventListener('click', () => this.open(log));

    const size = document.createElement('span');
    if (log.bytes === undefined) {
      size.className = 'caos-table__cell-note';
      size.textContent = 'unknown';
    } else {
      size.textContent = formatBytes(log.bytes);
    }

    return {
      id: log.id,
      selected: this.chosen.has(log.id),
      cells: {
        at: { node: when },
        person: log.person ?? '',
        application: log.application ?? '',
        operation: log.operation ?? '',
        status: log.status ?? '',
        bytes: { node: size },
        open: { node: open },
      },
    } as CaosTableRow;
  }
}

define('caos-log-list', CaosLogList);

declare global {
  interface HTMLElementTagNameMap {
    'caos-log-list': CaosLogList;
  }
}
